/**
 * JSON kept as site notes: the latest note under a topic is the current
 * value, attributed and dated by the platform; the browser keeps a copy so
 * the screen still works on the sample or when the platform refuses the
 * write. The caller is told which of the two took it.
 */
import type { DataSource } from "../../api/dataSource";

const local = (siteId: string, topic: string) => `slt.note.${siteId}.${topic}`;

export function readLocal<T>(siteId: string, topic: string): T | null {
  try {
    const raw = localStorage.getItem(local(siteId, topic));
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function writeLocal(siteId: string, topic: string, value: unknown): void {
  try {
    localStorage.setItem(local(siteId, topic), JSON.stringify(value));
  } catch {
    /* storage off */
  }
}

export async function latestNote<T>(
  source: DataSource,
  siteId: string,
  topic: string,
): Promise<{ value: T; by?: string; at?: string } | null> {
  try {
    const notes = await source.listNotes(siteId, topic);
    for (const n of notes) {
      try {
        return { value: JSON.parse(n.body) as T, by: n.createdBy, at: n.createdAt };
      } catch {
        /* not JSON */
      }
    }
  } catch {
    /* no platform */
  }
  const l = readLocal<T>(siteId, topic);
  return l ? { value: l } : null;
}

export async function putNote(source: DataSource, siteId: string, topic: string, value: unknown): Promise<"platform" | "browser"> {
  writeLocal(siteId, topic, value);
  try {
    await source.addNote(siteId, { topic, category: "recommendation", body: JSON.stringify(value) });
    return "platform";
  } catch {
    return "browser";
  }
}
